// src/contexts/CartContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem("bb_cart_v1");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Admin-configurable shipping settings
  const [shippingConfig, setShippingConfig] = useState(() => {
    try {
      const raw = localStorage.getItem("bb_shipping_config");
      return raw
        ? JSON.parse(raw)
        : { weightPerUnit: 850, shippingRate: 100 }; // default
    } catch {
      return { weightPerUnit: 850, shippingRate: 100 };
    }
  });

  useEffect(() => {
    localStorage.setItem("bb_cart_v1", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("bb_shipping_config", JSON.stringify(shippingConfig));
  }, [shippingConfig]);

  function addItem(product, qty = 1) {
    setCart(prev => {
      const found = prev.find(p => p.id === product.id);
      if (found) {
        return prev.map(p =>
          p.id === product.id
            ? { ...p, qty: Math.min((p.qty || 0) + qty, product.stock) }
            : p
        );
      }
      return [...prev, { ...product, qty: Math.min(qty, product.stock) }];
    });
  }

  function removeItem(id) {
    setCart(prev => prev.filter(p => p.id !== id));
  }

  function changeQty(id, delta) {
    setCart(prev =>
      prev.map(p => {
        if (p.id !== id) return p;
        const next = Math.max(1, p.qty + delta);
        return { ...p, qty: Math.min(next, p.stock) };
      })
    );
  }

  function clearCart() {
    setCart([]);
  }

  // Total price
  const total = cart.reduce((s, p) => s + p.price * (p.qty || 0), 0);
  // Total quantity
  const itemCount = cart.reduce((s, p) => s + (p.qty || 0), 0);
  // Total weight in grams
  const totalWeight = cart.reduce((s, p) => s + (p.weight || 0) * (p.qty || 0), 0);
  // Shipping charge
  const shippingCharge = Math.ceil(totalWeight / shippingConfig.weightPerUnit) * shippingConfig.shippingRate;
  // Grand total
  const grandTotal = total + shippingCharge;

  const value = {
    cart,
    addItem,
    removeItem,
    changeQty,
    clearCart,
    total,
    itemCount,
    totalWeight,
    shippingCharge,
    grandTotal,
    shippingConfig,
    setShippingConfig,
    setCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
